import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import { EnvironmentId, ThreadId } from "@cmmd-forge/contracts";
import { createFileRoute, notFound, retainSearchParams } from "@tanstack/react-router";
import { useMemo } from "react";

import { RemoteRuntimeThreadSurface } from "../components/chat/RemoteRuntimeThreadSurface";
import { resolveRemoteRuntimeThreadConnectionState } from "../components/chat/remoteRuntimeThreadRoute.logic";
import { useRemoteRuntimeThreadConnection } from "../components/chat/useRemoteRuntimeThreadConnection";
import {
  remoteThreadEnvironmentWaitingReason,
  useRemoteThreadExecutionIntent,
} from "../components/chat/useRemoteThreadExecutionIntent";
import { useRemoteThreadHandoff } from "../components/chat/useRemoteThreadHandoff";
import { canonicalComposerSubmissionAcknowledgements } from "../components/chat/canonicalComposerSubmissionAcknowledgements";
import { useCmmdRuntimeThreadReplicaSnapshot } from "../cmmd/cmmdRuntimeThreadReplicaResource";
import { parseDiffRouteSearch, type DiffRouteSearch } from "../diffRouteSearch";
import { selectThreadByRef, selectThreadExistsByRef, useStore } from "../store";
import { isThreadRouteEnvironmentId } from "./-threadRouteEnvironmentId";
import { LocalChatThreadRouteView } from "./-LocalChatThreadRouteView";
import { useObserveActiveRouteCloudRuntimeConnection } from "./ensureActiveRouteCloudRuntime";
import {
  cloudTargetEnvironmentId,
  executionIntentRuntimeId,
  relayRuntimeTarget,
  remoteThreadHandoffHistoryReady,
  resolveRuntimeAwareThreadConnectionState,
} from "./-runtimeAwareThreadLifecycle";
import { useRemoteThreadRouteIdentity } from "./useRemoteThreadRouteIdentity";
import { useRetainedLocalThreadSurface } from "./useRetainedLocalThreadSurface";

function RuntimeAwareChatThreadRouteView() {
  const environmentId = Route.useParams({ select: (params) => params.environmentId });
  const threadId = Route.useParams({ select: (params) => params.threadId });
  const search = Route.useSearch();
  const routeThreadRef = useMemo(
    () => scopeThreadRef(EnvironmentId.make(environmentId), ThreadId.make(threadId)),
    [environmentId, threadId],
  );
  const { remoteTarget, canonicalEnvironmentId } = useRemoteThreadRouteIdentity(routeThreadRef);
  const canonicalThreadRef =
    canonicalEnvironmentId === null
      ? null
      : scopeThreadRef(canonicalEnvironmentId, routeThreadRef.threadId);
  const cloudThreadExists = useStore((store) =>
    selectThreadExistsByRef(store, canonicalThreadRef ?? routeThreadRef),
  );
  const canonicalThread = useStore((store) => selectThreadByRef(store, canonicalThreadRef));
  const remoteRuntimeId = executionIntentRuntimeId(remoteTarget, environmentId);
  const replicaSnapshot = useCmmdRuntimeThreadReplicaSnapshot(remoteRuntimeId, threadId);
  const canonicalAcknowledgements = useMemo(
    () =>
      canonicalComposerSubmissionAcknowledgements({
        canonicalMessages: canonicalThread?.messages,
      }),
    [canonicalThread?.messages],
  );
  const handoffHistoryReady = remoteThreadHandoffHistoryReady(
    remoteTarget?.connectionKind ?? null,
    replicaSnapshot.kind,
  );
  const cloudEnvironmentId = cloudTargetEnvironmentId(remoteTarget, environmentId);
  const cloudConnection = useObserveActiveRouteCloudRuntimeConnection(cloudEnvironmentId);
  const relayTarget = relayRuntimeTarget(remoteTarget);
  const relayConnection = useRemoteRuntimeThreadConnection(relayTarget, {
    executionIntent: relayTarget !== null,
  });
  const observedConnectionState = resolveRemoteRuntimeThreadConnectionState({
    target: remoteTarget,
    cloudThreadExists,
    cloudConnection: cloudConnection.state,
    relayState: relayConnection.state,
  });
  const executionIntent = useRemoteThreadExecutionIntent({
    environmentId: routeThreadRef.environmentId,
    runtimeId: remoteRuntimeId,
    threadRef: routeThreadRef,
  });
  const retainedLocalSurface = useRetainedLocalThreadSurface({
    observedConnectionState,
    routeThreadRef,
  });

  const handoffError = useRemoteThreadHandoff({
    source: routeThreadRef,
    destination: canonicalThreadRef,
    destinationAcknowledgements: canonicalAcknowledgements,
    ready:
      canonicalThread !== undefined &&
      (executionIntent.state.kind === "ready" || executionIntent.state.kind === "idle") &&
      handoffHistoryReady,
  });
  const connectionState = resolveRuntimeAwareThreadConnectionState({
    handoffError,
    observed: observedConnectionState,
    executionIntent: executionIntent.state,
    retainedLocalSurface,
  });

  return (
    <RemoteRuntimeThreadSurface
      connectionState={connectionState}
      environmentId={routeThreadRef.environmentId}
      environmentWaitingReason={remoteThreadEnvironmentWaitingReason(executionIntent.state)}
      localSurface={
        <LocalChatThreadRouteView
          environmentIdParam={environmentId}
          threadIdParam={threadId}
          search={search}
        />
      }
      onExecutionIntent={executionIntent.request}
      remoteTarget={remoteTarget}
      threadId={threadId}
    />
  );
}

export const Route = createFileRoute("/_chat/$environmentId/$threadId")({
  beforeLoad: ({ params }) => {
    if (!isThreadRouteEnvironmentId(params.environmentId)) {
      throw notFound();
    }
  },
  validateSearch: (search) => parseDiffRouteSearch(search),
  search: {
    middlewares: [
      retainSearchParams<DiffRouteSearch>([
        "rightPanelOpen",
        "rightPanelTabs",
        "rightPanel",
        "diffTurnId",
        "diffFilePath",
        "previewTabId",
      ]),
    ],
  },
  component: RuntimeAwareChatThreadRouteView,
});
